import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';

const fetchImageAsDataUrl = async (url) => {
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const getImageFormat = (dataUrl) => {
  const s = String(dataUrl || '').toLowerCase();
  if (s.startsWith('data:image/png')) return 'PNG';
  if (s.startsWith('data:image/jpeg') || s.startsWith('data:image/jpg')) return 'JPEG';
  return 'PNG';
};

const makeTimestamp = () => format(new Date(), 'yyyyMMdd_HHmm');

const makeSafeFileName = (base, ext) => {
  const name = String(base || 'export')
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_\-]/g, '');
  return `${name}_${makeTimestamp()}.${ext}`;
};

const normalizeExportWidth = (value, fallback = 20) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Math.min(Math.max(parsed / 5.5, 18), 90);
};

const getPdfColumnWeight = (columnName) => {
  const name = String(columnName || '').toLowerCase();
  if (name.includes('subject')) return 2.4;
  if (name.includes('sender') || name.includes('recipient') || name.includes('to')) return 1.15;
  if (name.includes('remarks')) return 1.3;
  if (name.includes('date')) return 0.8;
  if (name.includes('diary') || name.includes('dispatch') || name.includes('receipt') || name.includes('number') || name.includes('no.')) return 0.95;
  if (name.includes('priority') || name.includes('status')) return 0.9;
  return 1;
};

const buildColumnStyleMap = (columns, data, metadata = {}) => {
  const manualWidths = metadata.columnWidths || {};
  let styleMap = {};

  if (Object.keys(manualWidths).length) {
    styleMap = Object.fromEntries(Object.entries(manualWidths)
      .filter(([key]) => !Number.isNaN(Number(key)))
      .map(([key, value]) => {
        const widthValue = value?.wch ?? value?.cellWidth ?? value;
        const numericValue = Number(widthValue);
        const columnName = columns[Number(key)]?.header || columns[Number(key)]?.accessorKey || '';
        return [Number(key), { cellWidth: normalizeExportWidth(numericValue > 0 ? (value?.wch ? numericValue * 8 : numericValue) : 24, 24), weight: getPdfColumnWeight(columnName) }];
      }));
  } else {
    columns.forEach((column, index) => {
      const values = [String(column.header || column.accessorKey || '').trim()];
      data.forEach((item) => {
        const rawValue = typeof column.accessorKey === 'function'
          ? column.accessorKey(item)
          : typeof column.accessorKey === 'string' && column.accessorKey.includes('.')
            ? column.accessorKey.split('.').reduce((acc, key) => (acc == null ? '' : acc[key]), item)
            : item?.[column.accessorKey] ?? '';
        values.push(String(rawValue ?? ''));
      });
      const longest = values.reduce((max, value) => Math.max(max, value.length), 0);
      const baseWidth = normalizeExportWidth(Math.max(longest * 4.8, 70), 24);
      styleMap[index] = { cellWidth: baseWidth, weight: getPdfColumnWeight(column.header || column.accessorKey || '') };
    });
  }

  return styleMap;
};

const fitColumnWidthsToPage = (columnStyles, doc) => {
  const pageWidth = (doc.internal.pageSize.width || doc.internal.pageSize.getWidth()) || 210;
  const usableWidth = Math.max(150, pageWidth - 24);

  const totalWeightedBase = Object.values(columnStyles).reduce((sum, style) => {
    const width = Number(style?.cellWidth ?? style?.wch ?? 0);
    const weight = Number(style?.weight ?? 1);
    return sum + (Number.isFinite(width) && width > 0 ? width * weight : 0);
  }, 0);

  if (!totalWeightedBase) return columnStyles;

  return Object.fromEntries(Object.entries(columnStyles).map(([key, style]) => {
    const width = Number(style?.cellWidth ?? style?.wch ?? 0);
    const weight = Number(style?.weight ?? 1);
    const safeWidth = Number.isFinite(width) && width > 0 ? width : 20;
    const nextValue = (safeWidth * weight / totalWeightedBase) * usableWidth;
    return [key, { ...style, cellWidth: Math.max(16, Math.min(nextValue, usableWidth * 0.72)) }];
  }));
};

const finalizePdfOutput = (doc, title, output) => {
  const fileName = makeSafeFileName(title || 'report', 'pdf');
  if (output === 'download' || !output) {
    doc.save(fileName);
    return { mode: 'download', fileName };
  }

  if (output === 'print') {
    try {
      doc.autoPrint();
    } catch {
      void 0;
    }
  }

  const blobUrl = doc.output('bloburl');
  const w = window.open(blobUrl, '_blank', 'noopener,noreferrer');
  if (!w && output === 'print') {
    // Popup blocked: provide a reliable fallback to avoid silent failure.
    doc.save(fileName);
    return { mode: 'print-fallback-download', fileName };
  }
  if (output === 'print' && w) {
    w.focus();
    setTimeout(() => {
      try {
        w.print();
      } catch {
        void 0;
      }
    }, 800);
    return { mode: 'print', fileName };
  }
  return { mode: 'open', fileName };
};

export const exportToPDF = async (data, columns, title = 'Report', metadata = {}) => {
  const pdfFormat = metadata.pdfFormat || metadata.pageSize || 'a4';
  const doc = new jsPDF({
    orientation: metadata.orientation || 'portrait',
    unit: 'mm',
    format: pdfFormat
  });
  const { startDate, endDate } = metadata;
  const output = metadata.output || 'download';
  const columnWidths = fitColumnWidthsToPage(buildColumnStyleMap(columns, data, metadata), doc);

  const officeName = metadata.officeName || '';
  const logoDataUrl = metadata.logoDataUrl || (await fetchImageAsDataUrl(metadata.logoUrl || '/logo.png'));
  const logoFormat = logoDataUrl ? getImageFormat(logoDataUrl) : null;

  const periodText = startDate && endDate 
    ? `Period: ${format(new Date(startDate), 'dd MMM yyyy')} - ${format(new Date(endDate), 'dd MMM yyyy')}`
    : `Date: ${format(new Date(), 'dd MMM yyyy')}`;

  const tableColumn = columns.map(col => col.header);
  const tableRows = [];

  data.forEach(item => {
    const rowData = columns.map(col => {
      // Handle nested properties (e.g., 'user.name') or simple accessors
      if (typeof col.accessorKey === 'function') {
        return col.accessorKey(item);
      }
      // Handle nested strings like 'user.name'
      if (typeof col.accessorKey === 'string' && col.accessorKey.includes('.')) {
        const keys = col.accessorKey.split('.');
        let value = item;
        for (const key of keys) {
            value = value ? value[key] : '';
        }
        return value;
      }
      return item[col.accessorKey] || '';
    });
    tableRows.push(rowData);
  });

  const headerHeight = 52;

  doc.autoTable({
    head: [tableColumn],
    body: tableRows,
    startY: headerHeight,
    tableWidth: 'wrap',
    theme: 'grid',
    headStyles: {
      fillColor: [15, 23, 42],
      textColor: 255,
      fontStyle: 'bold',
      halign: 'center',
      lineColor: [148, 163, 184],
      lineWidth: 0.45,
      fontSize: 8
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252]
    },
    styles: {
      fontSize: metadata.orientation === 'landscape' ? 7 : 8,
      cellPadding: 2.4,
      valign: 'middle',
      overflow: 'linebreak',
      textColor: [15, 23, 42],
      lineColor: [148, 163, 184],
      lineWidth: 0.45,
      font: 'helvetica'
    },
    columnStyles: columnWidths,
    didDrawPage: (data) => {
      const pageSize = doc.internal.pageSize;
      const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
      const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();

      doc.setDrawColor(203, 213, 225);
      doc.setLineWidth(0.5);
      doc.line(12, 12, pageWidth - 12, 12);
      doc.line(12, 46, pageWidth - 12, 46);

      if (logoDataUrl && logoFormat) {
        doc.addImage(logoDataUrl, logoFormat, 16, 16, 18, 18);
      }

      doc.setTextColor(15, 23, 42);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('OFFICIAL RECORD', pageWidth / 2, 23, { align: 'center' });

      if (officeName) {
        doc.setFontSize(15);
        doc.setFont('helvetica', 'bold');
        doc.text(officeName, pageWidth / 2, 32, { align: 'center' });
      }

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(title, pageWidth / 2, officeName ? 40 : 34, { align: 'center' });

      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      doc.text(periodText, pageWidth / 2, officeName ? 44 : 38, { align: 'center' });

      doc.setFontSize(7.5);
      doc.setTextColor(71, 85, 105);
      doc.text(`Generated: ${format(new Date(), 'dd MMM yyyy HH:mm')}`, pageWidth - 14, 20, { align: 'right' });

      doc.setFontSize(7.5);
      doc.setTextColor(71, 85, 105);
      doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
    },
    margin: { top: headerHeight }
  });

  return finalizePdfOutput(doc, title, output);
};

export const exportAddressBookBookletPDF = async (contacts, metadata = {}) => {
  const output = metadata.output || 'download';
  const title = metadata.title || 'Address Book';
  const officeName = metadata.officeName || '';

  const doc = new jsPDF('portrait');
  const logoDataUrl = metadata.logoDataUrl || (await fetchImageAsDataUrl(metadata.logoUrl || '/logo.png'));
  const logoFormat = logoDataUrl ? getImageFormat(logoDataUrl) : null;

  const pageSize = doc.internal.pageSize;
  const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
  const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();

  const margin = 14;
  const gap = 6;
  const top = 38;
  const bottom = 14;
  const colWidth = (pageWidth - margin * 2 - gap) / 2;

  const safe = (v) => String(v || '').trim();
  const sortKey = (c) => `${safe(c.category).toLowerCase()}|${safe(c.organizationName).toLowerCase()}|${safe(c.contactPerson).toLowerCase()}`;

  const rows = (Array.isArray(contacts) ? contacts : [])
    .filter(Boolean)
    .slice()
    .sort((a, b) => sortKey(a).localeCompare(sortKey(b)));

  const byCategory = new Map();
  for (const c of rows) {
    const k = safe(c.category) || 'Other';
    if (!byCategory.has(k)) byCategory.set(k, []);
    byCategory.get(k).push(c);
  }

  const drawHeader = () => {
    if (logoDataUrl && logoFormat) {
      doc.addImage(logoDataUrl, logoFormat, margin, 10, 12, 12);
    }
    doc.setTextColor(40, 40, 40);
    if (officeName) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(officeName, pageWidth / 2, 16, { align: 'center' });
    }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(title, pageWidth / 2, officeName ? 24 : 18, { align: 'center' });
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated: ${format(new Date(), 'dd MMM yyyy HH:mm')}`, pageWidth - margin, 10, { align: 'right' });
  };

  const drawFooter = () => {
    const str = 'Page ' + doc.internal.getNumberOfPages();
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(str, pageWidth / 2, pageHeight - 10, { align: 'center' });
  };

  const measureCardHeight = (contact) => {
    const name = safe(contact.organizationName) || safe(contact.contactPerson) || '-';
    const attn = safe(contact.organizationName) && safe(contact.contactPerson) ? `Attn: ${safe(contact.contactPerson)}` : '';
    const designation = safe(contact.designation);
    const email = safe(contact.email);
    const phone = safe(contact.phoneNumber);
    const address = safe(contact.address);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    const nameLines = doc.splitTextToSize(name, colWidth - 10);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const attnLines = attn ? doc.splitTextToSize(attn, colWidth - 10) : [];
    const desigLines = designation ? doc.splitTextToSize(designation, colWidth - 10) : [];
    const contactInfo = [email, phone].filter(Boolean).join(' | ');
    const infoLines = contactInfo ? doc.splitTextToSize(contactInfo, colWidth - 10) : [];
    const addrLines = address ? doc.splitTextToSize(address, colWidth - 10) : [];

    const lines = nameLines.length + attnLines.length + desigLines.length + infoLines.length + Math.min(addrLines.length, 4);
    return Math.max(30, 10 + lines * 4);
  };

  const drawCard = (contact, x, y, h) => {
    doc.setDrawColor(220, 225, 234);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(x, y, colWidth, h, 3, 3, 'FD');

    const padX = 5;
    let cursorY = y + 6;

    const name = safe(contact.organizationName) || safe(contact.contactPerson) || '-';
    const attn = safe(contact.organizationName) && safe(contact.contactPerson) ? `Attn: ${safe(contact.contactPerson)}` : '';
    const designation = safe(contact.designation);
    const email = safe(contact.email);
    const phone = safe(contact.phoneNumber);
    const address = safe(contact.address);

    doc.setTextColor(35, 35, 35);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    const nameLines = doc.splitTextToSize(name, colWidth - padX * 2);
    doc.text(nameLines, x + padX, cursorY);
    cursorY += nameLines.length * 4.2;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);

    if (attn) {
      const attnLines = doc.splitTextToSize(attn, colWidth - padX * 2);
      doc.text(attnLines, x + padX, cursorY);
      cursorY += attnLines.length * 4;
    }

    if (designation) {
      const desigLines = doc.splitTextToSize(designation, colWidth - padX * 2);
      doc.text(desigLines, x + padX, cursorY);
      cursorY += desigLines.length * 4;
    }

    const contactInfo = [email, phone].filter(Boolean).join(' | ');
    if (contactInfo) {
      const infoLines = doc.splitTextToSize(contactInfo, colWidth - padX * 2);
      doc.text(infoLines, x + padX, cursorY);
      cursorY += infoLines.length * 4;
    }

    if (address) {
      doc.setTextColor(70, 70, 70);
      const addrLines = doc.splitTextToSize(address, colWidth - padX * 2).slice(0, 4);
      doc.text(addrLines, x + padX, cursorY);
    }
  };

  drawHeader();
  let y = top;

  for (const [category, list] of byCategory.entries()) {
    const barH = 8;
    if (y + barH > pageHeight - bottom) {
      drawFooter();
      doc.addPage();
      drawHeader();
      y = top;
    }
    doc.setFillColor(238, 242, 255);
    doc.setDrawColor(220, 225, 234);
    doc.roundedRect(margin, y, pageWidth - margin * 2, barH, 3, 3, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(55, 65, 81);
    doc.text(category, margin + 6, y + 5.6);
    y += barH + 5;

    let col = 0;
    let rowHeight = 0;
    for (const c of list) {
      const h = measureCardHeight(c);
      const x = col === 0 ? margin : margin + colWidth + gap;
      rowHeight = col === 0 ? h : Math.max(rowHeight, h);

      if (col === 0 && y + h > pageHeight - bottom) {
        drawFooter();
        doc.addPage();
        drawHeader();
        y = top;
        doc.setFillColor(238, 242, 255);
        doc.setDrawColor(220, 225, 234);
        doc.roundedRect(margin, y, pageWidth - margin * 2, barH, 3, 3, 'FD');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(55, 65, 81);
        doc.text(category, margin + 6, y + 5.6);
        y += barH + 5;
      }

      drawCard(c, x, y, h);

      if (col === 1) {
        y += rowHeight + 6;
        col = 0;
        rowHeight = 0;
      } else {
        col = 1;
      }
    }
    if (col === 1) {
      y += rowHeight + 6;
    }
    y += 2;
  }

  drawFooter();
  return finalizePdfOutput(doc, title, output);
};

export const exportToExcel = (data, fileName = 'export', options = {}) => {
  const rows = Array.isArray(data) ? data : [];
  const columnWidths = options.columnWidths || {};
  const sanitized = rows.map((row) => Object.fromEntries(
    Object.entries(row || {}).map(([key, value]) => {
      if (value === null || value === undefined || value === '') return [key, ''];
      if (typeof value === 'number') return [key, value];
      if (typeof value === 'boolean') return [key, value ? 'Yes' : 'No'];
      if (value instanceof Date) return [key, format(value, 'dd MMM yyyy')];
      return [key, String(value)];
    })
  ));

  const ws = XLSX.utils.json_to_sheet(sanitized.length ? sanitized : [{}]);
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  const rowCount = range.e.r + 1;
  const colCount = range.e.c + 1;

  for (let row = 0; row < rowCount; row += 1) {
    for (let col = 0; col < colCount; col += 1) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
      const cell = ws[cellRef];
      if (!cell) continue;

      cell.s = {
        ...cell.s,
        border: {
          top: { style: 'thin', color: { rgb: 'E5E7EB' } },
          bottom: { style: 'thin', color: { rgb: 'E5E7EB' } },
          left: { style: 'thin', color: { rgb: 'E5E7EB' } },
          right: { style: 'thin', color: { rgb: 'E5E7EB' } }
        },
        alignment: { vertical: 'center', horizontal: 'left' }
      };

      if (row === 0) {
        cell.s = {
          ...cell.s,
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '1F2937' } },
          alignment: { horizontal: 'center', vertical: 'center' }
        };
      }
    }
  }

  ws['!cols'] = Array.from({ length: colCount }, (_, index) => {
    if (columnWidths[index] && Number.isFinite(Number(columnWidths[index].wch))) {
      return { wch: Math.min(Math.max(Number(columnWidths[index].wch), 12), 40) };
    }
    const entries = sanitized.map((row) => String(row[Object.keys(row)[index]] ?? '')).filter(Boolean);
    const maxWidth = entries.reduce((max, value) => Math.max(max, value.length), 12);
    return { wch: Math.min(Math.max(maxWidth + 2, 12), 40) };
  });
  ws['!freeze'] = { ySplit: 1 };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Data');
  XLSX.writeFile(wb, makeSafeFileName(fileName, 'xlsx'));
};

export const exportCaseToExcel = (caseData, fileName = 'case_export') => {
  const valueText = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (Array.isArray(value)) return value.map(item => valueText(item)).join(' | ');
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  const formatDateValue = (value, showTime = false) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return valueText(value);
    return format(date, showTime ? 'dd MMM yyyy HH:mm' : 'dd MMM yyyy');
  };

  const caseNumber = valueText(caseData?.diaryNumber || caseData?.dispatchNumber || caseData?.receiptNumber || caseData?.caseNumber || caseData?.id || '');
  const officeName = valueText(caseData?.officeName || caseData?.office || caseData?.sendingOffice || caseData?.officeOf || '');
  const subject = valueText(caseData?.subject || caseData?.description || caseData?.actionRequired || '');

  const summaryRows = [
    ['OFFICIAL CASE RECORD', ''],
    ['Office', officeName],
    ['Case Number', caseNumber],
    ['Subject', subject],
    ['Status', valueText(caseData?.status || '')],
    ['Priority', valueText(caseData?.priority || '')],
    ['Document Type', valueText(caseData?.documentType || caseData?.mode || caseData?.modeOfDispatch || '')],
    ['Sender / Recipient', valueText(caseData?.sender || caseData?.recipient || '')],
    ['Sender Department', valueText(caseData?.senderDept || caseData?.recipientDept || '')],
    ['Reference Number', valueText(caseData?.referenceNumber || caseData?.fileReference || '')],
    ['Date of Letter', formatDateValue(caseData?.dateOfLetter)],
    ['Date Received / Issued', formatDateValue(caseData?.dateReceived || caseData?.dateIssued)],
    ['Forwarded To', valueText(caseData?.forwardedToName || caseData?.forwardedTo || caseData?.distributedTo || '')],
    ['Forwarded At', formatDateValue(caseData?.forwardedAt, true)],
    ['Confidential', valueText(caseData?.isConfidential ? 'Yes' : 'No')],
    ['Confidentiality Level', valueText(caseData?.confidentialityLevel || '')],
    ['Approval Status', valueText(caseData?.approvalStatus || '')],
    ['Action Required', valueText(caseData?.actionRequired || '')],
    ['Remarks', valueText(caseData?.remarks || '')],
    ['Linked e-File', valueText(caseData?.linkedEFileNumber || caseData?.linkedEFileId || '')],
    ['Generated At', formatDateValue(new Date(), true)],
  ];

  const detailsRows = [
    ['Field', 'Value'],
    ['Office', officeName],
    ['Case Number', caseNumber],
    ['Subject', subject],
    ['Status', valueText(caseData?.status || '')],
    ['Priority', valueText(caseData?.priority || '')],
    ['Document Type', valueText(caseData?.documentType || caseData?.mode || caseData?.modeOfDispatch || '')],
    ['Sender', valueText(caseData?.sender || '')],
    ['Recipient', valueText(caseData?.recipient || '')],
    ['Sender Department', valueText(caseData?.senderDept || '')],
    ['Recipient Department', valueText(caseData?.recipientDept || '')],
    ['Sender Address', valueText(caseData?.senderAddress || caseData?.address || '')],
    ['Recipient Address', valueText(caseData?.recipientAddress || '')],
    ['Forward To', valueText(caseData?.forwardedToName || caseData?.forwardedTo || '')],
    ['Forwarded At', formatDateValue(caseData?.forwardedAt, true)],
    ['Date Received', formatDateValue(caseData?.dateReceived)],
    ['Date Issued', formatDateValue(caseData?.dateIssued)],
    ['Date of Letter', formatDateValue(caseData?.dateOfLetter)],
    ['Reference Number', valueText(caseData?.referenceNumber || caseData?.fileReference || '')],
    ['Confidential', valueText(caseData?.isConfidential ? 'Yes' : 'No')],
    ['Confidentiality Level', valueText(caseData?.confidentialityLevel || '')],
    ['Approval Status', valueText(caseData?.approvalStatus || '')],
    ['Action Required', valueText(caseData?.actionRequired || '')],
    ['Remarks', valueText(caseData?.remarks || '')],
    ['Linked e-File', valueText(caseData?.linkedEFileNumber || caseData?.linkedEFileId || '')],
    ['Created At', formatDateValue(caseData?.createdAt, true)],
    ['Updated At', formatDateValue(caseData?.updatedAt, true)],
  ];

  const linkedCases = Array.isArray(caseData?.linkedCases) ? caseData.linkedCases : [];
  const complianceRows = Array.isArray(caseData?.complianceLogs) ? caseData.complianceLogs : [];
  const historyRows = Array.isArray(caseData?.history) ? caseData.history : [];

  const makeTableSheet = (rows) => {
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [
      { wch: 32 },
      { wch: 90 }
    ];
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    return ws;
  };

  const buildCustomListSheet = (rows) => {
    const sheetRows = [['No.', 'Entry']];
    rows.forEach((row, index) => {
      sheetRows.push([index + 1, valueText(row)]);
    });

    const ws = XLSX.utils.aoa_to_sheet(sheetRows);
    ws['!cols'] = [
      { wch: 10 },
      { wch: 110 }
    ];
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    return ws;
  };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, makeTableSheet(summaryRows), 'Summary');
  XLSX.utils.book_append_sheet(wb, makeTableSheet(detailsRows), 'Details');

  if (linkedCases.length) {
    const linkRows = [['Type', 'Number', 'Relation', 'Notes']];
    linkedCases.forEach((link) => {
      linkRows.push([
        valueText(link?.type || ''),
        valueText(link?.number || link?.id || ''),
        valueText(link?.relationType || ''),
        valueText(link?.notes || link?.description || '')
      ]);
    });
    XLSX.utils.book_append_sheet(wb, makeTableSheet(linkRows), 'Linked Cases');
  }

  if (complianceRows.length) {
    const complianceRowsTable = [['Officer', 'Diary No.', 'Date', 'Notes']];
    complianceRows.forEach((log) => {
      complianceRowsTable.push([
        valueText(log?.officerName || log?.officer || log?.forwardedToName || ''),
        valueText(log?.diaryNumber || log?.diaryNo || log?.referenceNumber || ''),
        formatDateValue(log?.complianceDate || log?.date || log?.createdAt, true),
        valueText(log?.remarks || log?.notes || log?.comment || '')
      ]);
    });
    XLSX.utils.book_append_sheet(wb, makeTableSheet(complianceRowsTable), 'Compliance');
  }

  if (historyRows.length) {
    const historySheet = buildCustomListSheet(historyRows);
    XLSX.utils.book_append_sheet(wb, historySheet, 'History');
  }

  XLSX.writeFile(wb, makeSafeFileName(fileName, 'xlsx'));
};

